// src/components/contentGeneration/SourceSelectionStep.js
import React, { useState } from 'react';
import { Check, Search, Loader } from 'lucide-react';

const SourceSelectionStep = ({ entries, selectedSources, setSelectedSources, onNext, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const toggleSource = (entry) => {
    const isSelected = selectedSources.find(s => s.id === entry.id);
    if (isSelected) {
      setSelectedSources(selectedSources.filter(s => s.id !== entry.id));
    } else {
      setSelectedSources([...selectedSources, entry]);
    }
  };

  const filteredEntries = entries.filter(entry =>
    entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.authors.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Skeleton overlay while outlines are being generated
  if (loading) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 mb-4">
          Generating Outlines
        </h2>
        <p className="text-gray-600 mb-6">
          Analyzing your selected sources and drafting outline options. This usually takes 10-30 seconds.
        </p>

        <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-4 flex items-center space-x-3">
          <Loader className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
          <p className="text-sm font-medium text-primary-900">
            {selectedSources.length} {selectedSources.length === 1 ? 'source' : 'sources'} being analyzed...
          </p>
        </div>

        {/* Skeleton outline preview cards */}
        <div className="space-y-3 mb-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="border border-gray-200 rounded-lg p-4 bg-white"
            >
              <div className="animate-pulse space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded border-2 border-gray-200 bg-gray-100 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-1/4" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600">Step 1 of 6</p>
          <button
            disabled
            className="px-6 py-2 rounded-lg font-semibold flex items-center space-x-2 bg-gray-200 text-gray-400 cursor-not-allowed"
          >
            <Loader className="w-4 h-4 animate-spin" />
            <span>Generating Outlines...</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-secondary-900 mb-4">
        Select Your Sources
      </h2>
      <p className="text-gray-600 mb-6">
        Choose the sources you want to draw from for your content.
        Selected sources will be woven naturally into the generated piece.
      </p>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search sources by title or author..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {/* Selection summary */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-4">
        <p className="text-sm font-medium text-primary-900">
          {selectedSources.length} {selectedSources.length === 1 ? 'source' : 'sources'} selected
        </p>
        <p className="text-xs text-primary-700 mt-1">
          Select at least 2 sources. We recommend 3-15 for best results.
        </p>
      </div>

      {/* Sources list */}
      <div className="space-y-2 mb-6 max-h-[500px] overflow-y-auto">
        {filteredEntries.map(entry => {
          const isSelected = selectedSources.find(s => s.id === entry.id);
          return (
            <div
              key={entry.id}
              onClick={() => toggleSource(entry)}
              className={`border rounded-lg p-3 cursor-pointer transition-all ${
                isSelected
                  ? 'border-primary bg-primary-50'
                  : 'border-gray-200 hover:border-primary/40'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                  isSelected
                    ? 'bg-primary border-primary'
                    : 'border-gray-300'
                }`}>
                  {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                </div>

                <div className="flex-1 min-w-0 flex items-center justify-between">
                  <h3 className="font-semibold text-sm text-secondary-900 truncate">
                    {entry.title || 'Untitled'}
                  </h3>
                  <span className="text-xs text-gray-500 flex-shrink-0 ml-3">
                    {entry.authors || 'Unknown Author'} {entry.year ? `\u00B7 ${entry.year}` : ''}
                  </span>
                </div>
              </div>
              {(entry.journal || entry.researchFocus) && (
                <p className="text-xs text-gray-400 ml-7 mt-0.5 truncate">
                  {entry.journal || entry.researchFocus}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <p className="text-sm text-gray-600">
          Step 1 of 6
        </p>
        <button
          onClick={onNext}
          disabled={selectedSources.length < 2 || loading}
          className={`px-6 py-2 rounded-lg font-semibold transition-colors flex items-center space-x-2 ${
            selectedSources.length > 0 && !loading
              ? 'bg-primary text-white hover:bg-primary-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {loading ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Generating Outlines...</span>
            </>
          ) : (
            <span>Continue to Outline</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default SourceSelectionStep;
